import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Mail, Clock, Check, ArrowRight, AlertCircle } from "lucide-react";
import axios from "axios";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import { Reveal, MaskedLines } from "../components/motion";
import { COMPANY, SERVICES, IMG } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Phone, label: "Call Us", value: COMPANY.phone, href: COMPANY.phoneHref, id: "phone" },
  { icon: Mail, label: "Email Us", value: COMPANY.email, href: `mailto:${COMPANY.email}`, id: "email" },
  { icon: Clock, label: "Opening Hours", value: COMPANY.hours, href: null, id: "hours" },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.service || form.message.trim().length < 5) {
      setError("Please fill in your name, email, service and a short message.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError(
        err?.response?.data?.detail || "Something went wrong — please call us instead."
      );
    }
  };

  return (
    <>
      <SEO
        title="Contact & Free Quotes | Esskay Homes"
        description="Get a free, no-obligation quote from Esskay Homes. Loft conversions, kitchens, bathrooms, extensions, refurbishments and new builds across London & the South East."
        path="/contact"
      />

      <PageHero
        eyebrow="Contact & Free Quotes"
        title={["Let's talk about", <>your <em className="italic text-gold">home</em>.</>]}
        sub="Tell us what you're planning — we'll arrange a free site survey and send a detailed, itemised quotation. No pressure, no obligation."
        image={IMG.bathrooms}
        crumbs={["Contact"]}
      />

      <section className="container-ek grid gap-14 py-24 lg:grid-cols-[1fr_1.4fr] lg:gap-20 lg:py-32" data-testid="contact-section">
        {/* INFO */}
        <div>
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-gold" />
              Get In Touch
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
              We usually reply within{" "}
              <em className="italic text-gold">one working day</em>.
            </h2>
          </Reveal>

          <div className="mt-12 space-y-4">
            {INFO.map((c, i) => (
              <Reveal key={c.id} delay={0.15 + i * 0.07}>
                <div
                  data-testid={`contact-info-${c.id}`}
                  className="flex items-center gap-5 border border-white/10 bg-navy2/50 p-6 transition-colors duration-500 hover:border-gold/30"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-gold/40">
                    <c.icon className="h-5 w-5 text-gold" />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-mist">{c.label}</p>
                    {c.href ? (
                      <a
                        href={c.href}
                        data-testid={`contact-info-link-${c.id}`}
                        className="mt-1 block text-base text-white transition-colors hover:text-gold"
                      >
                        {c.value}
                      </a>
                    ) : (
                      <p className="mt-1 text-base text-white">{c.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.4}>
            <div className="mt-8 border-l-2 border-gold pl-6 text-sm leading-relaxed text-mist">
              Proudly serving Farnborough, Farnham, Guildford, Camberley,
              Woking, Reading, London & the wider South East.
            </div>
          </Reveal>
        </div>

        {/* FORM */}
        <Reveal delay={0.2}>
          <div
            data-testid="contact-form-panel"
            className="relative border border-white/10 bg-navy2/60 p-8 md:p-12"
          >
            <AnimatePresence mode="wait">
              {status === "sent" ? (
                <motion.div
                  key="sent"
                  data-testid="contact-success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="flex min-h-[480px] flex-col items-center justify-center text-center"
                >
                  <span className="flex h-20 w-20 items-center justify-center border border-gold bg-gold/10">
                    <Check className="h-9 w-9 text-gold" />
                  </span>
                  <h3 className="mt-8 font-serif text-3xl font-medium text-white md:text-4xl">
                    Thank you, {form.name.split(" ")[0]}.
                  </h3>
                  <p className="mt-4 max-w-sm text-sm leading-relaxed text-mist md:text-base">
                    Your enquiry about{" "}
                    <span className="text-gold">{form.service}</span> has been
                    received. A member of the team will be in touch within one
                    working day.
                  </p>
                  <button
                    data-testid="contact-reset-button"
                    onClick={() => {
                      setForm({ name: "", email: "", phone: "", service: "", message: "" });
                      setStatus("idle");
                    }}
                    className="link-underline mt-8 text-[12px] font-medium uppercase tracking-[0.22em] text-gold"
                  >
                    Send Another Enquiry
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.5 }}
                  noValidate
                >
                  <MaskedLines
                    lines={[
                      <span key="t" className="font-serif text-3xl font-medium text-white md:text-4xl">
                        Request your <em className="italic text-gold">free quote</em>
                      </span>,
                    ]}
                  />
                  <p className="mt-3 text-sm text-mist">
                    Fields marked * are required.
                  </p>

                  <div className="mt-10 grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="cf-name" className="field-label">Full Name *</label>
                      <input
                        id="cf-name"
                        data-testid="contact-name-input"
                        className="field"
                        placeholder="Jane Smith"
                        value={form.name}
                        onChange={set("name")}
                      />
                    </div>
                    <div>
                      <label htmlFor="cf-email" className="field-label">Email Address *</label>
                      <input
                        id="cf-email"
                        data-testid="contact-email-input"
                        type="email"
                        className="field"
                        placeholder="jane@example.co.uk"
                        value={form.email}
                        onChange={set("email")}
                      />
                    </div>
                    <div>
                      <label htmlFor="cf-phone" className="field-label">Phone Number</label>
                      <input
                        id="cf-phone"
                        data-testid="contact-phone-input"
                        type="tel"
                        className="field"
                        placeholder="07123 456 789"
                        value={form.phone}
                        onChange={set("phone")}
                      />
                    </div>
                    <div>
                      <label htmlFor="cf-service" className="field-label">Service Required *</label>
                      <div className="relative">
                        <select
                          id="cf-service"
                          data-testid="contact-service-select"
                          className={`field appearance-none pr-10 ${form.service ? "text-ink" : "text-slate-500"}`}
                          value={form.service}
                          onChange={set("service")}
                        >
                          <option value="" disabled className="bg-navy">
                            Select a service…
                          </option>
                          {SERVICES.map((s) => (
                            <option key={s.slug} value={s.name} className="bg-navy">
                              {s.name}
                            </option>
                          ))}
                          <option value="Something else" className="bg-navy">
                            Something else
                          </option>
                        </select>
                        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gold">
                          ▾
                        </span>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="cf-message" className="field-label">Your Project *</label>
                      <textarea
                        id="cf-message"
                        data-testid="contact-message-input"
                        rows="5"
                        className="field resize-none"
                        placeholder="Tell us about the space, your ideas and rough timescales…"
                        value={form.message}
                        onChange={set("message")}
                      />
                    </div>
                  </div>

                  {error && (
                    <p
                      data-testid="contact-error"
                      className="mt-6 flex items-center gap-2.5 border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm text-red-300"
                    >
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={status === "sending"}
                    className="btn-gold group mt-10 w-full disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "sending" ? "Sending…" : "Send My Enquiry"}
                    {status !== "sending" && (
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    )}
                  </button>
                  <p className="mt-5 text-center text-[11px] uppercase tracking-[0.18em] text-slate-600">
                    Free survey · No-obligation quote · 12-month warranty
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </section>

    </>
  );
};

export default Contact;
